import { v4 as uuidv4 } from 'uuid'
import { clearStore, Stores } from '../domain/db'
import { ExpenseDao } from '../domain/expense_dao'
import { CategoryDao } from '../domain/category_dao'
import { CurrencyDao } from '../domain/currency_dao'
import { Category, Currency, Expense } from '../domain/model'

export interface ParsedRow {
  date: string
  time: string
  category: string
  value: number
  currency: string
}

export interface ParseResult {
  categories: string[]
  currencies: string[]
  rows: ParsedRow[]
}

const EXPECTED_HEADER = 'date,time,category,value,currency'

export function parseDateTime(dateStr: string, timeStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  const [h, min, s] = timeStr.split(':').map(Number)
  if (
    [y, m, d, h, min, s].some((n) => n === undefined || isNaN(n)) ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > 31
  ) {
    throw new Error(`Invalid date/time: ${dateStr} ${timeStr}`)
  }
  const date = new Date(Date.UTC(y, m - 1, d, h, min, s))
  return date.getTime()
}

export function parseCSV(csvText: string): ParseResult {
  const trimmed = csvText.trim()
  if (trimmed === '') {
    return { categories: [], currencies: [], rows: [] }
  }

  const lines = trimmed.split(/\r?\n/)
  if (lines.length < 1) return { categories: [], currencies: [], rows: [] }

  const header = lines[0].trim()
  if (header !== EXPECTED_HEADER) {
    throw new Error(`Invalid CSV header. Expected: ${EXPECTED_HEADER}`)
  }

  const rows: ParsedRow[] = []
  const categorySet = new Set<string>()
  const currencySet = new Set<string>()

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (line === '') continue

    const parts = line.split(',')
    if (parts.length !== 5) {
      throw new Error(`Invalid row ${i + 1}: expected 5 columns`)
    }

    const [date, time, category, valueStr, currency] = parts
    const value = parseFloat(valueStr)
    if (isNaN(value)) {
      throw new Error(`Invalid value in row ${i + 1}: ${valueStr}`)
    }

    rows.push({ date, time, category, value, currency })
    categorySet.add(category)
    currencySet.add(currency)
  }

  return {
    categories: Array.from(categorySet),
    currencies: Array.from(currencySet),
    rows,
  }
}

export class CSVImporter {
  constructor(
    private readonly _expenseDao: ExpenseDao,
    private readonly _categoryDao: CategoryDao,
    private readonly _currencyDao: CurrencyDao
  ) {}

  public import = async (csvText: string): Promise<void> => {
    const parsed = parseCSV(csvText)

    await clearStore(Stores.Expenses)
    await clearStore(Stores.Categories)

    // Build category name -> Category
    const categoryByName = new Map<string, Category>()
    for (const name of parsed.categories) {
      const cat = new Category(name)
      await this._categoryDao.add(cat)
      categoryByName.set(name, cat)
    }

    // Build currency id (3-letter) -> Currency from static data; persist selected ids so they appear in picker
    const currencyById = new Map<string, Currency>();
    for (const id of parsed.currencies) {
      const w = await this._currencyDao.getById(id);
      if (w) {
        const cur = new Currency(w.id, w.name, w.sign)
        currencyById.set(w.id, cur);
      }
    }

    for (const row of parsed.rows) {
      const category = categoryByName.get(row.category)
      const currency = currencyById.get(row.currency.toUpperCase())
      if (!category || !currency) continue;

      const created = parseDateTime(row.date, row.time)
      const expense: Expense = {
        id: uuidv4(),
        created,
        currency_id: currency.id,
        value: row.value,
        category_id: category.id,
      }
      await this._expenseDao.add(expense)
    }
  }
}
